import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, urlImagem, comprimirImagem } from '../lib/api';
import { useAuth } from '../lib/auth';
import QRPrintModal from '../components/QRPrintModal';
import QRPrintModalLote from '../components/QRPrintModalLote';

interface Unidade { id: number; nome: string; }
interface Piso { id: number; unidade_id: number; nome: string; }
interface Local { id: number; unidade_id: number; piso_id: number; nome: string; }
interface Categoria { id: number; nome: string; tipo: string; }
interface Cliente { id: number; nome: string; status?: string; }
interface Ativo {
  id: number;
  unidade_id: number | null;
  piso_id: number | null;
  local_id: number | null;
  categoria_id: number | null;
  nome: string;
  patrimonio: string | null;
  marca: string | null;
  fabricante: string | null;
  modelo: string | null;
  numero_serie: string | null;
  qr_code: string | null;
  foto_url: string | null;
  status: string;
  cliente_ids?: number[];
  clientes?: Array<{ id: number; nome: string }>;
}

// Consulta reversa "equipamento -> plano PMOC". Um equipamento pertence a no
// maximo UM plano vivo (indice unico uq_pmoc_ativos_ativo_ativo), e antes disso
// so era possivel descobrir onde ele estava recebendo o erro na cara. Aqui a
// informacao fica disponivel para consulta, sem depender de tentar gerar um plano.
interface PmocDoAtivo {
  ativo_id: number;
  plano_id: number;
  plano_nome: string | null;
  ambiente_nome?: string | null;
}

const ITENS_POR_PAGINA = 5;

function label<T extends { id: number; nome: string }>(lista: T[], id: number | null | undefined): string {
  return lista.find((i) => i.id === Number(id))?.nome || '-';
}

function msgErro(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function ordenarAtivosPorNome(lista: Ativo[]): Ativo[] {
  return [...lista].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' }));
}

// `embutido`: renderizado dentro de outra pagina (aba "Equipamentos" de Cadastros).
// Nesse modo o titulo vira subtitulo e o bloco "Clientes do equipamento" nao aparece.
export default function Ativos({ embutido = false }: { embutido?: boolean }) {
  const { isPremium, isConsumoDisponivel, user } = useAuth();
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [pisos, setPisos] = useState<Piso[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [filtroUnidadeId, setFiltroUnidadeId] = useState('');
  const [filtroPisoId, setFiltroPisoId] = useState('');
  const [filtroLocalId, setFiltroLocalId] = useState('');
  const [filtroNome, setFiltroNome] = useState('');
  const [form, setForm] = useState<Partial<Ativo>>({ status: 'ativo' });
  const [foto, setFoto] = useState<File | null>(null);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [qrAtivo, setQrAtivo] = useState<Ativo | null>(null);
  const [qrPublicoAtivo, setQrPublicoAtivo] = useState<Ativo | null>(null);
  const [qrLoteAberto, setQrLoteAberto] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [pmocPorAtivo, setPmocPorAtivo] = useState<Record<number, PmocDoAtivo>>({});
  // Só mostra a coluna se a consulta funcionou. Empresa sem PMOC recebe 403 e a
  // tabela fica exatamente como era.
  const [temPmoc, setTemPmoc] = useState(false);

  // QR embute o tenant (empresa_id) para não colidir entre bases de clientes diferentes.
  const qrUrl = (a: Ativo) => {
    const codigo = (a.qr_code || '').trim();
    const empresaId = user?.empresa_id ?? '';
    const base = `${window.location.origin}${window.location.pathname}`;
    return `${base}#/app/qr/${empresaId}/${encodeURIComponent(codigo)}`;
  };

  const qrPublicoUrl = (a: Ativo) => {
    const codigo = (a.qr_code || '').trim();
    const empresaId = user?.empresa_id ?? '';
    const base = `${window.location.origin}${window.location.pathname}`;
    return `${base}#/equipamento-publico/${empresaId}/${encodeURIComponent(codigo)}`;
  };

  const editando = Boolean(form.id);
  const pisosDaUnidade = form.unidade_id ? pisos.filter((p) => p.unidade_id === Number(form.unidade_id)) : pisos;
  const locaisDoPiso = form.piso_id ? locais.filter((l) => l.piso_id === Number(form.piso_id)) : locais;
  const tiposChamado = categorias;
  const ocultarTipoChamado = Boolean(user?.pode_retirar_tipo_chamado_para_equipamento);
  // Localizacao sai da tela por DOIS motivos: o fluxo de OS simplificada (que ja
  // nao usa hierarquia) ou a permissao que retira unidade/local/piso de vez.
  const localizacaoOpcionalEquipamento = Boolean(user?.criacao_os_simplificada_habilitado)
    || Boolean(user?.retirar_unidade_local_piso_habilitado);
  // Permissão "VER EQUIPAMENTOS NAS ORDENS DE SERVIÇO": libera vincular clientes ao equipamento.
  const podeVincularClientes = Boolean(user?.pode_ver_equipamentos_nas_os) && !embutido;
  // "RETIRAR TELA PMOC" derruba tudo de PMOC nesta tela: a coluna e o atalho.
  const semPmoc = Boolean(user?.retirar_tela_pmoc_habilitado);
  const mostrarPlanoPmoc = embutido && Boolean(user?.criacao_os_simplificada_habilitado) && !semPmoc;
  const podeQrPublico = Boolean(user?.qrcode_detalhes_habilitado);
  const ativosComQrPublico = ativos.filter((a) => a.qr_code && a.status !== 'inativo');
  const clientesSelecionados = form.cliente_ids || [];
  const termoCliente = buscaCliente.trim().toLowerCase();
  const clientesVisiveis = termoCliente
    ? clientes.filter((c) => c.nome.toLowerCase().includes(termoCliente))
    : clientes;
  const pisosFiltro = filtroUnidadeId
    ? pisos.filter((p) => p.unidade_id === Number(filtroUnidadeId))
    : pisos;
  const locaisFiltro = filtroPisoId
    ? locais.filter((l) => l.piso_id === Number(filtroPisoId))
    : (filtroUnidadeId ? locais.filter((l) => l.unidade_id === Number(filtroUnidadeId)) : locais);
  const termoFiltroNome = filtroNome.trim().toLowerCase();
  const ativosFiltrados = ativos.filter((a) => {
    if (!localizacaoOpcionalEquipamento && filtroUnidadeId && String(a.unidade_id) !== filtroUnidadeId) return false;
    if (!localizacaoOpcionalEquipamento && filtroPisoId && String(a.piso_id) !== filtroPisoId) return false;
    if (!localizacaoOpcionalEquipamento && filtroLocalId && String(a.local_id) !== filtroLocalId) return false;
    if (termoFiltroNome && !a.nome.toLowerCase().includes(termoFiltroNome)) return false;
    return true;
  });
  const totalPaginas = Math.max(1, Math.ceil(ativosFiltrados.length / ITENS_POR_PAGINA));
  const paginaSegura = Math.min(paginaAtual, totalPaginas);
  const ativosPaginados = ativosFiltrados.slice((paginaSegura - 1) * ITENS_POR_PAGINA, paginaSegura * ITENS_POR_PAGINA);

  async function carregar() {
    setErro('');
    const [a, u, p, l, c, cli, pmoc] = await Promise.all([
      api<Ativo[]>('/ativos/ativos.php'),
      api<Unidade[]>('/cadastros/unidades.php?somente_ativos=1'),
      api<Piso[]>('/cadastros/pisos.php?somente_ativos=1'),
      api<Local[]>('/cadastros/locais.php?somente_ativos=1'),
      api<Categoria[]>('/cadastros/categorias.php?somente_ativos=1'),
      podeVincularClientes ? api<Cliente[]>('/cadastros/clientes.php?somente_ativos=1') : Promise.resolve([] as Cliente[]),
      // Só na aba de Cadastros com a permissão ativa. Falha não derruba a tela:
      // a coluna simplesmente não aparece.
      mostrarPlanoPmoc ? api<PmocDoAtivo[]>('/pmoc/ativos.php').catch(() => null) : Promise.resolve(null),
    ]);
    setAtivos(ordenarAtivosPorNome(a));
    setUnidades(u);
    setPisos(p);
    setLocais(l);
    setCategorias(c);
    setClientes(cli);
    if (pmoc) {
      const mapa: Record<number, PmocDoAtivo> = {};
      pmoc.forEach((r) => { mapa[Number(r.ativo_id)] = r; });
      setPmocPorAtivo(mapa);
      setTemPmoc(true);
    } else {
      setPmocPorAtivo({});
      setTemPmoc(false);
    }
  }

  function alternarCliente(clienteId: number) {
    setForm((atual) => {
      const atuais = atual.cliente_ids || [];
      return {
        ...atual,
        cliente_ids: atuais.includes(clienteId) ? atuais.filter((id) => id !== clienteId) : [...atuais, clienteId],
      };
    });
  }

  useEffect(() => {
    if (isPremium) carregar().catch((e) => setErro(msgErro(e)));
  }, [isPremium, podeVincularClientes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (paginaAtual > totalPaginas) {
      setPaginaAtual(totalPaginas);
    }
  }, [paginaAtual, totalPaginas]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroUnidadeId, filtroPisoId, filtroLocalId, filtroNome]);

  function limpar() {
    setForm({ status: 'ativo' });
    setFoto(null);
    setBuscaCliente('');
  }

  function limparFiltros() {
    setFiltroUnidadeId('');
    setFiltroPisoId('');
    setFiltroLocalId('');
    setFiltroNome('');
  }

  async function subirFoto(id: number, arquivo: File) {
    const fd = new FormData();
    fd.append('id', String(id));
    fd.append('foto', await comprimirImagem(arquivo));
    await api('/ativos/foto.php', { method: 'POST', body: fd, isForm: true });
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    setOcupado(true);
    try {
      const idOuNull = (v: unknown) => v ? Number(v) : null;
      const body = {
        id: form.id,
        nome: (form.nome || '').trim(),
        unidade_id: localizacaoOpcionalEquipamento ? null : idOuNull(form.unidade_id),
        piso_id: localizacaoOpcionalEquipamento ? null : idOuNull(form.piso_id),
        local_id: localizacaoOpcionalEquipamento ? null : idOuNull(form.local_id),
        categoria_id: form.categoria_id ? Number(form.categoria_id) : null,
        patrimonio: form.patrimonio || null,
        marca: form.marca || null,
        fabricante: form.fabricante || null,
        modelo: form.modelo || null,
        numero_serie: form.numero_serie || null,
        status: form.status || 'ativo',
        ...(podeVincularClientes ? { cliente_ids: clientesSelecionados } : {}),
      };
      let id = form.id;
      if (id) await api('/ativos/ativos.php', { method: 'PUT', body });
      else {
        const criado = await api<{ id: number }>('/ativos/ativos.php', { method: 'POST', body });
        id = criado.id;
      }
      if (foto && id) await subirFoto(Number(id), foto);
      setMsg(editando ? 'Equipamento atualizado.' : 'Equipamento criado.');
      limpar();
      await carregar();
    } catch (e2) {
      setErro(msgErro(e2));
    } finally {
      setOcupado(false);
    }
  }

  // Equipamento nao se exclui: OS, PMOC e relatorios antigos apontam para ele.
  // Desativar tira o item das novas selecoes e preserva o historico.
  async function alternarStatus(a: Ativo) {
    const on = (a.status || 'ativo') === 'ativo';
    const proximo = on ? 'inativo' : 'ativo';
    if (!window.confirm(`Deseja ${on ? 'desativar' : 'reativar'} "${a.nome}"?`)) return;
    setErro('');
    setMsg('');
    try {
      await api('/ativos/ativos.php', { method: 'PUT', body: { id: a.id, status: proximo } });
      setMsg(on ? 'Equipamento desativado.' : 'Equipamento reativado.');
      await carregar();
    } catch (e) {
      setErro(msgErro(e));
    }
  }

  async function excluir(id: number) {
    if (!window.confirm('Excluir este equipamento?')) return;
    setErro('');
    setMsg('');
    try {
      await api('/ativos/ativos.php?id=' + id, { method: 'DELETE' });
      setMsg('Equipamento excluído.');
      await carregar();
    } catch (e) {
      setErro(msgErro(e));
    }
  }

  async function fotoLinha(id: number, arquivo: File | null) {
    if (!arquivo) return;
    setErro('');
    setMsg('');
    try {
      await subirFoto(id, arquivo);
      setMsg('Foto atualizada.');
      await carregar();
    } catch (e) {
      setErro(msgErro(e));
    }
  }

  if (!isPremium) {
    return (
      <div className="card">
        <h2>Equipamentos e QR Code</h2>
        <p style={{ color: 'var(--texto-suave)' }}>Cadastro de equipamentos, fotos e QR Code fazem parte do plano Premium.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="titulo-pg">
        {embutido ? <h3 style={{ margin: 0 }}>Equipamentos</h3> : <h1>Equipamentos</h1>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {podeQrPublico && (
            <button className="btn sec sm" onClick={() => setQrLoteAberto(true)}>Emitir QR Code de todos os equipamentos</button>
          )}
          <button className="btn sec sm" onClick={() => carregar().catch((e) => setErro(msgErro(e)))}>Atualizar</button>
        </div>
      </div>
      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <form className="card" onSubmit={salvar} style={{ marginBottom: 16 }}>
        <h3>{editando ? 'Editar equipamento' : 'Novo equipamento'}</h3>

        {!localizacaoOpcionalEquipamento && (
          <>
        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--texto-suave)', margin: '18px 2px 8px' }}>
          Localização
        </div>
        <div className="grid cols2">
          <div>
            <label>Unidade{localizacaoOpcionalEquipamento ? '' : ' *'}</label>
            <select
              value={form.unidade_id || ''}
              onChange={(e) => setForm({ ...form, unidade_id: e.target.value ? Number(e.target.value) : undefined, piso_id: undefined, local_id: undefined })}
              required={!localizacaoOpcionalEquipamento}
            >
              <option value="">Selecione</option>
              {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </div>
          <div>
            <label>Piso{localizacaoOpcionalEquipamento ? '' : ' *'}</label>
            <select
              value={form.piso_id || ''}
              onChange={(e) => setForm({ ...form, piso_id: e.target.value ? Number(e.target.value) : undefined, local_id: undefined })}
              required={!localizacaoOpcionalEquipamento}
            >
              <option value="">Selecione</option>
              {pisosDaUnidade.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <div>
            <label>Local{localizacaoOpcionalEquipamento ? '' : ' *'}</label>
            <select
              value={form.local_id || ''}
              onChange={(e) => setForm({ ...form, local_id: e.target.value ? Number(e.target.value) : undefined })}
              required={!localizacaoOpcionalEquipamento}
            >
              <option value="">Selecione</option>
              {locaisDoPiso.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </div>
        </div>
          </>
        )}

        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--texto-suave)', margin: '18px 2px 8px' }}>Identificação</div>
        <div className="grid cols2">
          <div>
            <label>Nome *</label>
            <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
          </div>
          {!ocultarTipoChamado && (
            <div>
              <label>Tipo de chamado *</label>
              <select
                value={form.categoria_id || ''}
                onChange={(e) => setForm({ ...form, categoria_id: e.target.value ? Number(e.target.value) : null })}
                required
              >
                <option value="">Selecione o tipo de chamado</option>
                {tiposChamado.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          )}
          <div>
            <label>Patrimônio</label>
            <input className="input" value={form.patrimonio || ''} onChange={(e) => setForm({ ...form, patrimonio: e.target.value })} />
          </div>
          <div>
            <label>Marca</label>
            <input className="input" value={form.marca || ''} onChange={(e) => setForm({ ...form, marca: e.target.value })} />
          </div>
          <div>
            <label>Fabricante</label>
            <input className="input" value={form.fabricante || ''} onChange={(e) => setForm({ ...form, fabricante: e.target.value })} />
          </div>
          <div>
            <label>Modelo</label>
            <input className="input" value={form.modelo || ''} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
          </div>
          <div>
            <label>{user?.rotulo_numero_serie || 'Número de série'}</label>
            <input className="input" value={form.numero_serie || ''} onChange={(e) => setForm({ ...form, numero_serie: e.target.value })} />
          </div>
        </div>

        {podeVincularClientes && (
          <>
            <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--texto-suave)', margin: '18px 2px 8px' }}>Clientes do equipamento</div>
            <p style={{ color: 'var(--texto-suave)', margin: '0 2px 10px', fontSize: 13 }}>
              Marque um ou mais clientes. Eles passam a ver este equipamento nas ordens de serviço e no Portal do Cliente.
            </p>
            {!clientes.length ? (
              <p className="vazio" style={{ padding: 12, fontSize: 13 }}>
                Nenhum cliente cadastrado ainda. Cadastre em Cadastros &gt; Clientes.
              </p>
            ) : (
              <>
                <input
                  className="input"
                  value={buscaCliente}
                  onChange={(e) => setBuscaCliente(e.target.value)}
                  placeholder="Buscar cliente pelo nome"
                />
                <div
                  style={{
                    marginTop: 8,
                    maxHeight: 200,
                    overflowY: 'auto',
                    border: '1px solid var(--cinza-borda)',
                    borderRadius: 12,
                    padding: 10,
                    display: 'grid',
                    gap: 6,
                  }}
                >
                  {clientesVisiveis.map((c) => (
                    <label key={c.id} className="check-line" style={{ gap: 10, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={clientesSelecionados.includes(c.id)}
                        onChange={() => alternarCliente(c.id)}
                      />
                      <span>{c.nome}</span>
                    </label>
                  ))}
                  {!clientesVisiveis.length && (
                    <span style={{ color: 'var(--texto-suave)', fontSize: 13 }}>Nenhum cliente com esse nome.</span>
                  )}
                </div>
                <p style={{ color: 'var(--texto-suave)', margin: '8px 2px 0', fontSize: 13 }}>
                  {clientesSelecionados.length
                    ? `${clientesSelecionados.length} cliente(s) selecionado(s).`
                    : 'Nenhum cliente selecionado - o equipamento fica sem dono definido.'}
                </p>
              </>
            )}
          </>
        )}

        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--texto-suave)', margin: '18px 2px 8px' }}>Foto</div>
        <div className="grid cols2">
          <div>
            <label>Foto do equipamento</label>
            <input className="input" type="file" accept="image/*" onChange={(e) => setFoto(e.target.files?.[0] || null)} />
          </div>
          <div>
            {foto && <img src={URL.createObjectURL(foto)} alt="" className="thumb" style={{ marginTop: 22 }} />}
          </div>
        </div>

        <div className="form-actions">
          {editando && <button type="button" className="btn sec" onClick={limpar}>Cancelar</button>}
          <button className="btn" disabled={ocupado}>{ocupado ? 'Salvando...' : (editando ? 'Salvar equipamento' : 'Adicionar equipamento')}</button>
        </div>
      </form>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="titulo-pg" style={{ marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>Filtros rápidos</h3>
          <button type="button" className="btn sec sm" onClick={limparFiltros}>Limpar filtros</button>
        </div>
        <div className="grid cols2">
          <div>
            <label>Nome do equipamento</label>
            <input
              className="input"
              value={filtroNome}
              onChange={(e) => setFiltroNome(e.target.value)}
              placeholder="Buscar pelo nome"
            />
          </div>
          {!localizacaoOpcionalEquipamento && (
            <>
          <div>
            <label>Unidade</label>
            <select
              value={filtroUnidadeId}
              onChange={(e) => {
                setFiltroUnidadeId(e.target.value);
                setFiltroPisoId('');
                setFiltroLocalId('');
              }}
            >
              <option value="">Todas</option>
              {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </div>
          <div>
            <label>Piso</label>
            <select
              value={filtroPisoId}
              onChange={(e) => {
                setFiltroPisoId(e.target.value);
                setFiltroLocalId('');
              }}
            >
              <option value="">Todos</option>
              {pisosFiltro.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <div>
            <label>Local</label>
            <select value={filtroLocalId} onChange={(e) => setFiltroLocalId(e.target.value)}>
              <option value="">Todos</option>
              {locaisFiltro.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </div>
            </>
          )}
        </div>
      </div>

      {mostrarPlanoPmoc && temPmoc && (
        <p className="muted" style={{ margin: '0 0 8px', fontSize: 12 }}>
          <strong>Plano PMOC</strong>: um equipamento pertence a um plano por vez. <strong>Livre</strong> significa
          que ele ainda não está em nenhum; com o nome de um plano, ele está nesse — e ao gerar um plano novo para
          o cliente dele, é transferido automaticamente.
        </p>
      )}

      <div className="tabela-wrap">
        <table>
          <thead>
            <tr>
              <th>Equipamento</th>
              {!localizacaoOpcionalEquipamento && <th>Localização</th>}
              <th>Tipo de chamado</th>
              {embutido && <th>Situação</th>}
              {mostrarPlanoPmoc && temPmoc && <th>Plano PMOC</th>}
              {podeVincularClientes && <th>Clientes</th>}
              <th>QR</th>
              <th>Foto</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {ativosPaginados.map((a) => (
              <tr key={a.id}>
                <td><strong>{a.nome}</strong><br /><small>{a.patrimonio || a.marca || a.modelo || '-'}</small></td>
                {!localizacaoOpcionalEquipamento && <td>{label(unidades, a.unidade_id)} / {label(pisos, a.piso_id)} / {label(locais, a.local_id)}</td>}
                <td>{label(tiposChamado, a.categoria_id)}</td>
                {embutido && (
                  <td>
                    <span
                      className="badge"
                      style={(a.status || 'ativo') === 'ativo'
                        ? { background: '#f0fdf4', color: '#166534', border: '1px solid #16a34a33' }
                        : { background: '#fef2f2', color: '#b91c1c', border: '1px solid #dc262633' }}
                    >
                      {(a.status || 'ativo') === 'ativo' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                )}
                {mostrarPlanoPmoc && temPmoc && (
                  <td>
                    {pmocPorAtivo[a.id]
                      ? <span title="Ao gerar um plano novo para o cliente, ele é transferido para o plano novo">
                          {pmocPorAtivo[a.id].plano_nome || 'plano #' + pmocPorAtivo[a.id].plano_id}
                        </span>
                      : <span style={{ color: 'var(--texto-suave)' }}>Livre</span>}
                  </td>
                )}
                {podeVincularClientes && (
                  <td>
                    {a.clientes?.length
                      ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{a.clientes.map((c) => <span key={c.id} className="badge">{c.nome}</span>)}</div>
                      : <span style={{ color: 'var(--texto-suave)' }}>Sem cliente</span>}
                  </td>
                )}
                <td><code>{a.qr_code || '-'}</code></td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                    {a.foto_url ? <a href={urlImagem(a.foto_url)} target="_blank" rel="noreferrer"><img src={urlImagem(a.foto_url)} alt="" className="thumb" /></a> : <span style={{ color: 'var(--texto-suave)' }}>-</span>}
                    <input className="input mini-file" type="file" accept="image/*" onChange={(e) => fotoLinha(a.id, e.target.files?.[0] || null)} />
                  </div>
                </td>
                <td className="acoes">
                  {a.qr_code && <button className="btn sm" onClick={() => setQrAtivo(a)}>QR</button>}
                  {a.qr_code && podeQrPublico && <button className="btn sec sm" onClick={() => setQrPublicoAtivo(a)}>QR público</button>}
                  {isConsumoDisponivel && <Link className="btn sec sm" to={`/app/registro-consumo/${a.id}`}>Registro de consumo</Link>}
                  {/* Na aba de Cadastros o PMOC e gerido pelo plano do cliente: este
                      atalho levaria para o modulo antigo, fora do contexto. */}
                  {!mostrarPlanoPmoc && !semPmoc && <Link className="btn sec sm" to={`/app/pmoc/ativo/${a.id}`}>PMOC</Link>}
                  <button className="btn sec sm" onClick={() => { setForm({ ...a, cliente_ids: a.cliente_ids || [] }); setBuscaCliente(''); }}>Editar</button>
                  {/* Na aba de Cadastros o equipamento so se ativa/desativa. Excluir
                      apagaria um registro que OS e planos PMOC referenciam. */}
                  {embutido ? (
                    <button
                      className={`btn ${(a.status || 'ativo') === 'ativo' ? 'danger' : 'sec'} sm`}
                      title={(a.status || 'ativo') === 'ativo'
                        ? 'Deixa de aparecer em novas OS e novos planos; o histórico continua'
                        : 'Volta a aparecer nas novas seleções'}
                      onClick={() => alternarStatus(a)}
                    >
                      {(a.status || 'ativo') === 'ativo' ? 'Desativar' : 'Ativar'}
                    </button>
                  ) : (
                    <button className="btn danger sm" onClick={() => excluir(a.id)}>Excluir</button>
                  )}
                </td>
              </tr>
            ))}
            {!ativosFiltrados.length && (
              <tr>
                <td colSpan={(podeVincularClientes ? 7 : 6) - (localizacaoOpcionalEquipamento ? 1 : 0) + (mostrarPlanoPmoc && temPmoc ? 1 : 0) + (embutido ? 1 : 0)} className="vazio">
                  {ativos.length ? 'Nenhum equipamento encontrado com os filtros aplicados.' : 'Nenhum equipamento cadastrado.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {ativosFiltrados.length > ITENS_POR_PAGINA && (
        <div className="paginacao-lista">
          <span>Página {paginaSegura} de {totalPaginas}</span>
          <div className="paginacao-acoes">
            <button className="btn sec sm" disabled={paginaSegura <= 1} onClick={() => setPaginaAtual(1)}>Primeira</button>
            <button className="btn sec sm" disabled={paginaSegura <= 1} onClick={() => setPaginaAtual((atual) => Math.max(1, atual - 1))}>Anterior</button>
            <button className="btn sec sm" disabled={paginaSegura >= totalPaginas} onClick={() => setPaginaAtual((atual) => Math.min(totalPaginas, atual + 1))}>Próxima</button>
            <button className="btn sec sm" disabled={paginaSegura >= totalPaginas} onClick={() => setPaginaAtual(totalPaginas)}>Última</button>
          </div>
        </div>
      )}

      {qrAtivo && (
        <QRPrintModal
          code={qrAtivo.qr_code || ''}
          title={qrAtivo.nome}
          subtitle={[qrAtivo.patrimonio, qrAtivo.marca].filter(Boolean).join(' - ')}
          value={qrUrl(qrAtivo)}
          onClose={() => setQrAtivo(null)}
        />
      )}

      {qrPublicoAtivo && (
        <QRPrintModal
          code={qrPublicoAtivo.qr_code || ''}
          title={`${qrPublicoAtivo.nome} - QR público`}
          subtitle={[
            qrPublicoAtivo.patrimonio,
            ...(localizacaoOpcionalEquipamento ? [] : [
              label(unidades, qrPublicoAtivo.unidade_id),
              label(pisos, qrPublicoAtivo.piso_id),
              label(locais, qrPublicoAtivo.local_id),
            ]),
          ].filter((item) => item && item !== '-').join(' - ')}
          value={qrPublicoUrl(qrPublicoAtivo)}
          onClose={() => setQrPublicoAtivo(null)}
        />
      )}

      {qrLoteAberto && (
        <QRPrintModalLote
          itens={ativosComQrPublico.map((a) => ({
            id: a.id,
            titulo: `${a.nome} - QR público`,
            subtitulo: [
              a.patrimonio,
              ...(localizacaoOpcionalEquipamento ? [] : [
                label(unidades, a.unidade_id),
                label(pisos, a.piso_id),
                label(locais, a.local_id),
              ]),
            ].filter((item) => item && item !== '-').join(' - '),
            value: qrPublicoUrl(a),
          }))}
          onClose={() => setQrLoteAberto(false)}
        />
      )}
    </div>
  );
}
